import { ref } from 'vue';
import type { Table } from '@/services/tableService';
import type { Order } from '@/services/orderService';

interface UseTablesViewOrdersParams {
  getOpenOrderForTable: (tableId: number) => Promise<Order | null>;
  getOrderDetails: (orderId: number) => Promise<Order>;
  refreshTablesData: () => void;
}

export function useTablesViewOrders({
  getOpenOrderForTable,
  getOrderDetails,
  refreshTablesData
}: UseTablesViewOrdersParams) {
  const showOrderModal = ref(false);
  const selectedTableForOrder = ref<Table | null>(null);
  const orderModalMode = ref<'create' | 'edit'>('create');
  const orderToEdit = ref<Order | null>(null);

  const showOrderDetailsModal = ref(false);
  const selectedOrderForDetails = ref<Order | null>(null);

  const withOpenOrder = async (
    table: Table,
    onFound: (openOrder: Order) => Promise<void> | void
  ) => {
    try {
      const openOrder = await getOpenOrderForTable(table.id);

      if (!openOrder) {
        console.warn('No open order found for table:', table.id);
        return;
      }

      await onFound(openOrder);
    } catch (err) {
      console.error('Error fetching order for table:', err);
    }
  };

  const handleNewOrder = (table: Table) => {
    selectedTableForOrder.value = table;
    orderModalMode.value = 'create';
    orderToEdit.value = null;
    showOrderModal.value = true;
  };

  const handleEditOrder = async (table: Table) => {
    await withOpenOrder(table, (openOrder) => {
      selectedTableForOrder.value = table;
      orderModalMode.value = 'edit';
      orderToEdit.value = openOrder;
      showOrderModal.value = true;
    });
  };


  const handleViewBill = async (table: Table) => {
    await withOpenOrder(table, async (openOrder) => {
      const fullOrderDetails = await getOrderDetails(openOrder.id);
      selectedOrderForDetails.value = fullOrderDetails;
      showOrderDetailsModal.value = true;
    });
  };

  const closeOrderModal = () => {
    showOrderModal.value = false;
    orderModalMode.value = 'create';
    orderToEdit.value = null;
  };


  const closeOrderDetailsModal = () => {
    showOrderDetailsModal.value = false;
  };

  const handleOrderCreated = () => {
    refreshTablesData();
  };

  const handleOrderStatusUpdate = () => {
    refreshTablesData();
  };

  const handlePaymentCompleted = () => {
    refreshTablesData();
    closeOrderDetailsModal();
  };


  return {
    showOrderModal,
    selectedTableForOrder,
    orderModalMode,
    orderToEdit,
    showOrderDetailsModal,
    selectedOrderForDetails,
    handleNewOrder,
    handleEditOrder,
    handleViewBill,
    closeOrderModal,
    closeOrderDetailsModal,
    handleOrderCreated,
    handleOrderStatusUpdate,
    handlePaymentCompleted
  };
}
